import { useEffect, useMemo, useRef } from "react";
import uPlot, { type AlignedData, type Options } from "uplot";
import "uplot/dist/uPlot.min.css";

interface Props {
  xs: Float64Array | number[];
  ys: Float64Array | number[];
  xLabel: string;
  yLabel: string;
  title: string;
  namedPeaks?: { index: number; x: number; name: string }[];
  highlightedX?: number | null;
  onHoverPoint?: (point: { idx: number; x: number; y: number } | null) => void;
}

/**
 * Chromatogram plot. Wraps uPlot in a ResizeObserver-driven host so it
 * always fills its parent. Equivalent to giu's `g.Plot` panel in the
 * original GUI, with the same axis labels.
 */
export function Chromatogram(props: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const namedPeakLayerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const markerDotRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const highlightedXRef = useRef<number | null>(null);
  const updateHighlightRef = useRef<() => void>(() => {});

  const data: AlignedData = useMemo(() => {
    return [
      props.xs instanceof Float64Array ? props.xs : Float64Array.from(props.xs),
      props.ys instanceof Float64Array ? props.ys : Float64Array.from(props.ys),
    ];
  }, [props.xs, props.ys]);

  // (Re)create the plot when host or data identity changes.
  useEffect(() => {
    const host = hostRef.current;
    const tooltip = tooltipRef.current;
    const namedPeakLayer = namedPeakLayerRef.current;
    const marker = markerRef.current;
    const markerDot = markerDotRef.current;
    if (!host || !tooltip || !namedPeakLayer || !marker || !markerDot) return;

    const updateNamedPeakLines = () => {
      const plot = plotRef.current;
      namedPeakLayer.replaceChildren();
      if (!plot || !props.namedPeaks?.length) return;

      const min = plot.scales.x.min;
      const max = plot.scales.x.max;
      const hostRect = host.getBoundingClientRect();
      const plotRect = plot.over.getBoundingClientRect();
      const top = plotRect.top - hostRect.top;

      for (const peak of props.namedPeaks) {
        if (min != null && max != null && (peak.x < min || peak.x > max)) {
          continue;
        }

        const line = document.createElement("div");
        line.className = "chart-named-peak-line";
        line.style.left = `${plotRect.left - hostRect.left + plot.valToPos(peak.x, "x")}px`;
        line.style.top = `${top}px`;
        line.style.height = `${plotRect.height}px`;
        line.title = `#${peak.index + 1} ${peak.name}`;

        const label = document.createElement("div");
        label.className = "chart-named-peak-label";
        label.textContent = `#${peak.index + 1} ${peak.name}`;
        line.appendChild(label);

        namedPeakLayer.appendChild(line);
      }
    };

    const updateHighlight = () => {
      const plot = plotRef.current;
      const highlightedX = highlightedXRef.current;
      if (!plot || highlightedX == null) {
        marker.style.display = "none";
        return;
      }

      const idx = nearestIdx(data[0], highlightedX);
      const x = data[0][idx];
      const y = data[1][idx];
      if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y)) {
        marker.style.display = "none";
        return;
      }

      const min = plot.scales.x.min;
      const max = plot.scales.x.max;
      if (min != null && max != null && (x < min || x > max)) {
        marker.style.display = "none";
        return;
      }

      const hostRect = host.getBoundingClientRect();
      const plotRect = plot.over.getBoundingClientRect();
      const left = plotRect.left - hostRect.left + plot.valToPos(x, "x");
      const top = plotRect.top - hostRect.top;

      marker.style.display = "block";
      marker.style.left = `${left}px`;
      marker.style.top = `${top}px`;
      marker.style.height = `${plotRect.height}px`;
      markerDot.style.top = `${plot.valToPos(y, "y")}px`;
    };

    const updateOverlays = () => {
      updateNamedPeakLines();
      updateHighlight();
    };
    updateHighlightRef.current = updateHighlight;

    const hideTooltip = () => {
      tooltip.style.display = "none";
      props.onHoverPoint?.(null);
    };

    const wheelZoom = (e: WheelEvent) => {
      const plot = plotRef.current;
      if (!plot) return;
      const xs = data[0];
      const fullMin = Number(xs[0]);
      const fullMax = Number(xs[xs.length - 1]);
      const min = plot.scales.x.min ?? fullMin;
      const max = plot.scales.x.max ?? fullMax;
      const fullRange = fullMax - fullMin;
      const range = max - min;
      if (fullRange <= 0 || range <= 0) return;

      const rect = plot.over.getBoundingClientRect();
      const left = e.clientX - rect.left;
      if (left < 0 || left > rect.width) return;

      e.preventDefault();
      const anchor = plot.posToVal(left, "x");
      const zoom = Math.exp(e.deltaY * 0.001);
      const nextRange = Math.min(fullRange, Math.max(fullRange / xs.length, range * zoom));
      if (nextRange >= fullRange * 0.999) {
        plot.setScale("x", { min: fullMin, max: fullMax });
        return;
      }

      const pct = (anchor - min) / range;
      let nextMin = anchor - pct * nextRange;
      let nextMax = nextMin + nextRange;
      if (nextMin < fullMin) {
        nextMax += fullMin - nextMin;
        nextMin = fullMin;
      }
      if (nextMax > fullMax) {
        nextMin -= nextMax - fullMax;
        nextMax = fullMax;
      }
      plot.setScale("x", { min: nextMin, max: nextMax });
    };

    const showTooltip = (u: uPlot) => {
      const idx = u.cursor.idxs?.[1] ?? u.cursor.idx;
      if (idx == null) {
        hideTooltip();
        return;
      }

      const x = data[0][idx];
      const y = data[1][idx];
      if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y)) {
        hideTooltip();
        return;
      }

      props.onHoverPoint?.({ idx, x, y });

      tooltip.innerHTML = `<div>${props.xLabel}: ${formatTooltipValue(x)}</div><div>${props.yLabel}: ${formatTooltipValue(y)}</div>`;
      tooltip.style.display = "block";

      const hostRect = host.getBoundingClientRect();
      const plotRect = u.over.getBoundingClientRect();
      const pointLeft = plotRect.left - hostRect.left + u.valToPos(x, "x");
      const pointTop = plotRect.top - hostRect.top + u.valToPos(y, "y");
      const pad = 8;
      const gap = 12;
      let left = pointLeft + gap;
      let top = pointTop - tooltip.offsetHeight - gap;

      if (left + tooltip.offsetWidth + pad > hostRect.width) {
        left = pointLeft - tooltip.offsetWidth - gap;
      }
      if (top < pad) top = pointTop + gap;
      if (top + tooltip.offsetHeight + pad > hostRect.height) {
        top = hostRect.height - tooltip.offsetHeight - pad;
      }

      tooltip.style.left = `${Math.max(pad, left)}px`;
      tooltip.style.top = `${Math.max(pad, top)}px`;
    };

    const opts: Options = {
      title: props.title,
      width: host.clientWidth || 600,
      height: host.clientHeight || 400,
      padding: [10, 12, 8, 8],
      scales: { x: { time: false } },
      axes: [
        { label: props.xLabel, stroke: "#cbd5e1", grid: { stroke: "#2a2e36" } },
        { label: props.yLabel, stroke: "#cbd5e1", grid: { stroke: "#2a2e36" } },
      ],
      series: [
        { label: props.xLabel },
        {
          label: "signal",
          stroke: "#4a90e2",
          width: 1,
        },
      ],
      cursor: { drag: { x: true, y: false } },
      hooks: {
        setCursor: [showTooltip],
        setScale: [updateOverlays],
        setSize: [updateOverlays],
      },
    };

    plotRef.current?.destroy();
    const plot = new uPlot(opts, data, host);
    plotRef.current = plot;
    updateOverlays();
    plot.over.addEventListener("mouseleave", hideTooltip);
    plot.over.addEventListener("wheel", wheelZoom, { passive: false });

    const ro = new ResizeObserver(() => {
      const p = plotRef.current;
      if (p) p.setSize({ width: host.clientWidth, height: host.clientHeight });
    });
    ro.observe(host);

    return () => {
      plot.over.removeEventListener("mouseleave", hideTooltip);
      plot.over.removeEventListener("wheel", wheelZoom);
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
      updateHighlightRef.current = () => {};
    };
  }, [data, props.title, props.xLabel, props.yLabel, props.namedPeaks, props.onHoverPoint]);

  useEffect(() => {
    highlightedXRef.current = props.highlightedX ?? null;
    updateHighlightRef.current();
  }, [props.highlightedX]);

  return (
    <div ref={hostRef} className="uplot-host">
      <div ref={namedPeakLayerRef} className="chart-named-peak-layer" aria-hidden="true" />
      <div ref={markerRef} className="chart-highlight-marker" aria-hidden="true">
        <div className="chart-highlight-line" />
        <div ref={markerDotRef} className="chart-highlight-dot" />
      </div>
      <div ref={tooltipRef} className="chart-tooltip" aria-hidden="true" />
    </div>
  );
}

function nearestIdx(xs: AlignedData[0], x: number): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < xs.length; i++) {
    const d = Math.abs(Number(xs[i]) - x);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

function formatTooltipValue(v: number): string {
  if (Math.abs(v) >= 1000 || (Math.abs(v) > 0 && Math.abs(v) < 0.001)) {
    return v.toExponential(3);
  }
  return v.toFixed(3);
}
